import React, { useEffect, useState } from 'react';
import Loading from '../../components/Loading';

/**
 * **계정 관리 — 사이트 운영자 전용 창** (데스크톱 길드원 탭).
 *
 * 이 사이트에는 **비밀번호가 없다.** 로그인 창에 아이디 한 줄을 넣으면 들어와지고, 그 뒤로는
 * uuid 쿠키가 신분증이다. 그래서 "비밀번호 변경" 이라는 것이 아예 없고, 누가 로그인을 못 하게
 * 됐을 때 손댈 곳은 **아이디**뿐이다 — 아이디가 곧 열쇠다.
 *
 * 그래서 이 창이 하는 일은 둘뿐이다:
 *   아이디 바꾸기   남이 알아 버렸거나, 본인이 기억하는 이름으로 바꿔 줄 때
 *   추방 / 되살리기 잘못 내보냈거나 돌아온 사람
 *
 * 직급은 게임 등급을 따라 자동으로 맞춰지므로 여기서 건드리지 않는다 — 바꿔 봐야 다음
 * 명단 동기화에 덮어써진다.
 *
 * 막는 것은 서버가 한다(`siteAdmin` 검사). 여기서 숨기는 것은 눈에 안 띄게 하려는 것뿐이다.
 */

const CARD: React.CSSProperties = {
    background: '#fff',
    borderRadius: 18,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    padding: '26px 24px 20px',
    // 폰에서도 연다(모바일 길드원 탭의 [관리]) — 420 고정이면 화면 밖으로 나간다
    minWidth: 'min(420px, 92vw)',
    maxWidth: '92vw',
    boxSizing: 'border-box',
    maxHeight: '86vh',
    overflowY: 'auto',
    position: 'relative'
};

const BACKDROP: React.CSSProperties = {
    position: 'fixed',
    left: 0,
    top: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 2100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const FIELD: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 10,
    border: '1.5px solid #dfe3e8',
    fontSize: 15,
    outline: 'none',
    background: '#fff',
    color: '#222'
};

function button(color: string, on = true): React.CSSProperties {
    return {
        border: 'none',
        borderRadius: 10,
        padding: '10px 16px',
        fontSize: 14,
        fontWeight: 700,
        color: '#fff',
        background: on ? color : '#cfd8dc',
        cursor: on ? 'pointer' : 'not-allowed',
        whiteSpace: 'nowrap'
    };
}

function CloseButton({ onClick }: { onClick: () => void }) {
    return (
        <button
            onClick={onClick}
            aria-label="닫기"
            style={{
                position: 'absolute', right: 14, top: 12, border: 'none', background: 'none',
                fontSize: 22, lineHeight: 1, color: '#999', cursor: 'pointer'
            }}
        >×</button>
    );
}

/** 서버가 보낸 말을 그대로 띄운다 — 무엇이 막혔는지는 서버가 제일 잘 안다 */
function Note({ text, bad }: { text: string; bad?: boolean }) {
    if (!text) return null;
    return (
        <div style={{
            marginTop: 10, padding: '9px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
            color: bad ? '#c62828' : '#1b5e20',
            background: bad ? 'rgba(198,40,40,0.08)' : 'rgba(27,94,32,0.08)',
            border: `1px solid ${bad ? 'rgba(198,40,40,0.25)' : 'rgba(27,94,32,0.25)'}`
        }}>{text}</div>
    );
}

/** 켜고 끄는 스위치 한 줄 (상대 룬·시뮬 공용) */
function Switch({ label, desc, on, busy, onClick }: { label: string; desc: string; on: boolean; busy: boolean; onClick: () => void }) {
    return (
        <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea' }}>{label}</div>
                <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginTop: 2 }}>{desc}</div>
            </div>
            <button
                onClick={onClick}
                disabled={busy}
                role="switch"
                aria-checked={on}
                title={on ? '끄기' : '켜기'}
                style={{
                    position: 'relative', width: 56, height: 30, borderRadius: 15, border: 'none', flexShrink: 0,
                    background: on ? '#43a047' : '#c5ccd6', cursor: busy ? 'default' : 'pointer',
                    transition: 'background 0.15s', opacity: busy ? 0.6 : 1
                }}
            >
                <span style={{
                    position: 'absolute', top: 3, left: on ? 29 : 3, width: 24, height: 24, borderRadius: 12,
                    background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', transition: 'left 0.15s'
                }} />
            </button>
            <span style={{ width: 28, fontSize: 13, fontWeight: 800, color: on ? '#43a047' : '#999' }}>{on ? 'ON' : 'OFF'}</span>
        </div>
    );
}

export interface AdminTarget {
    uuid: string;
    nickname: string;
    role?: string;
    profileImg?: string;
    /** 상대 덱 룬 열람 — 켜고 끄는 사람(애플톡)에게만 내려온다 */
    opponentRunes?: boolean;
    /** 시뮬레이션 탭 보기 — 켜고 끄는 사람(애플톡)에게만 내려온다 */
    simViewer?: boolean;
    /** false = 아직 본인이 가입하지 않은 계정(명단 동기화가 만들어 둔 빈 계정) — 머리에 [미가입] 표시 */
    registered?: boolean;
}

/**
 * 한 사람의 계정을 손보는 창.
 *
 * `onChanged` 는 목록을 다시 받으라는 신호다 — 추방하거나 스위치를 바꾸면 목록의 값이
 * 낡는다.
 *
 * 아이디는 보이지 않는다 — 아이디가 비밀번호 몫이라 운영자도 남의 아이디를 못 보고 못 정한다. 잊었으면 [아이디 초기화]로
 * 무작위 아이디를 만들어 **한 번만** 보여 주고, 운영자가 본인에게 알려 준다 (사용자 2026-10-27 "지금 아이디 못보게 만들고
 * 새 아이디 대신 아이디 초기화 버튼을 만들어. 아이디가 랜덤으로 초기화").
 */
export function MemberAdmin({
    target, onClose, onChanged, runeGranter, accountAdmin = true
}: {
    target: AdminTarget;
    onClose: () => void;
    onChanged: () => void;
    /** 상대 덱 룬 열람을 켜고 끌 수 있는 사람(애플톡·뭐해?)인가 — 서버도 따로 막는다 */
    runeGranter?: boolean;
    /**
     * 계정을 손볼 수 있나(사이트 운영자). 아니면 **방덱 룬 스위치만** 보인다 — 뭐해? 처럼 스위치만 쓰는 사람에게
     * 아이디 바꾸기·추방을 보이지 않는다(서버도 막는다).
     */
    accountAdmin?: boolean;
}) {
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');
    /** 방금 초기화한 아이디 — 이 창에서 한 번만 보인다(닫으면 다시 볼 수 없다) */
    const [resetId, setResetId] = useState('');
    const [copied, setCopied] = useState(false);
    const [runesOn, setRunesOn] = useState(!!target.opponentRunes);
    const [simOn, setSimOn] = useState(!!target.simViewer);

    // 스위치 하나 — 상대 룬·시뮬 둘 다 같은 모양. path 로 API 를 고르고, 응답의 field 값으로 상태를 맞춘다
    const toggle = async (path: string, field: 'opponentRunes' | 'simViewer', cur: boolean, set: (v: boolean) => void) => {
        const on = !cur;
        setBusy(true); setMsg(''); setErr('');
        try {
            const res = await fetch(path, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ uuid: target.uuid, on })
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setErr(data.error || '바꾸지 못했습니다.');
            } else {
                set(!!data[field]);
                onChanged();
            }
        } catch {
            setErr('서버에 닿지 못했습니다.');
        } finally {
            setBusy(false);
        }
    };
    const toggleRunes = () => toggle('/api/auth/opponent-runes', 'opponentRunes', runesOn, setRunesOn);
    const toggleSim = () => toggle('/api/auth/sim-viewer', 'simViewer', simOn, setSimOn);

    const resetUserId = async () => {
        if (!window.confirm(`${target.nickname} 님의 아이디를 무작위로 초기화할까요?\n\n지금 아이디로는 더 로그인할 수 없습니다. 새 아이디를 본인에게 알려 주세요.`)) return;
        setBusy(true); setMsg(''); setErr(''); setCopied(false);
        try {
            const res = await fetch('/api/auth/user-id/reset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ uuid: target.uuid })
            });
            const data = await res.json();
            if (!res.ok || !data.success || !data.userId) {
                setErr(data.error || '초기화하지 못했습니다.');
            } else {
                setResetId(data.userId);
                onChanged();
            }
        } catch {
            setErr('서버에 닿지 못했습니다.');
        } finally {
            setBusy(false);
        }
    };
    const copyResetId = async () => {
        try { await navigator.clipboard.writeText(resetId); setCopied(true); } catch { setCopied(false); }
    };

    const kick = async () => {
        if (!window.confirm(`${target.nickname} 님을 추방할까요?\n\n글과 전적은 남고 로그인만 막힙니다. [추방된 계정] 에서 되살릴 수 있습니다.`)) return;
        setBusy(true); setMsg(''); setErr('');
        try {
            const res = await fetch('/api/auth/user', {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ uuid: target.uuid })
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setErr(data.error || '추방하지 못했습니다.');
            } else {
                onChanged();
                onClose();
            }
        } catch {
            setErr('서버에 닿지 못했습니다.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={BACKDROP} onClick={onClose}>
            <div style={CARD} onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <img loading="lazy"
                        src={target.profileImg || '/images/default-profile.png'}
                        alt=""
                        style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', background: '#e3f0ff' }}
                        onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                    />
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ fontSize: 18, fontWeight: 800, color: '#1976d2' }}>{target.nickname}</div>
                            {/* 미가입 계정 표시(사용자 2026-10-29) — 명단 동기화가 만든 빈 계정이라 본인이 아직 로그인한 적이 없다 */}
                            {target.registered === false && (
                                <span style={{
                                    fontSize: 11.5, fontWeight: 800, color: '#e65100', background: '#fff3e0',
                                    border: '1px solid #ffcc80', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap'
                                }}>미가입</span>
                            )}
                        </div>
                        <div style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>
                            {target.registered === false ? '아직 사이트에 가입하지 않은 길드원입니다' : '계정 관리'}
                        </div>
                    </div>
                </div>

                {accountAdmin && (<>
                <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea' }}>아이디</div>
                        <div style={{ fontSize: 12.5, color: '#888', fontWeight: 600, marginTop: 2 }}>아이디를 잊었을 경우 초기화해주세요</div>
                    </div>
                    <button onClick={resetUserId} disabled={busy} style={button('#1976d2', !busy)}>아이디 초기화</button>
                </div>

                {resetId && (
                    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: '#f4f7fb', border: '1px solid #e3e8ef' }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea', marginBottom: 6 }}>새 아이디 — 본인에게 알려 주세요 (창을 닫으면 다시 볼 수 없습니다)</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, fontSize: 18, fontWeight: 800, letterSpacing: 1, color: '#222', fontFamily: 'monospace', wordBreak: 'break-all' }}>{resetId}</div>
                            <button onClick={copyResetId} style={button('#667eea', true)}>{copied ? '복사됨' : '복사'}</button>
                        </div>
                    </div>
                )}
                </>)}

                {runeGranter && (<>
                    <Switch label="상대 방덱 룬 보기" desc="전투기록에서 상대 덱 몹의 룬을 볼 수 있습니다" on={runesOn} busy={busy} onClick={toggleRunes} />
                    <Switch label="시뮬레이션 탭 보기" desc="도구 탭의 전투 시뮬레이션을 쓸 수 있습니다" on={simOn} busy={busy} onClick={toggleSim} />
                </>)}

                <Note text={err} bad />
                <Note text={msg} />

                {accountAdmin && (<>
                <div style={{ height: 1, background: '#eef1f5', margin: '18px 0 12px' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 12.5, color: '#888', fontWeight: 600, flex: 1 }}>
                        추방하면 로그인만 막힙니다. 글·전적은 그대로 남습니다.
                    </div>
                    <button onClick={kick} disabled={busy} style={button('#d32f2f', !busy)}>추방</button>
                </div>
                </>)}
            </div>
        </div>
    );
}

interface DeletedRow {
    uuid: string;
    userId: string;
    nickname: string;
    role?: string;
    profileImg?: string;
    deletedAt?: string;
    /** 추방될 때 아이디를 비켜 준 계정 — 되살리려면 아이디를 새로 정해야 한다 */
    idReleased?: boolean;
}

/** 아이디 꼬리(`...#deleted-xxxx`)를 뗀 이름. 되살릴 때 기본값으로 쓴다 */
function baseId(userId: string): string {
    return String(userId || '').split('#deleted-')[0];
}

/** 되살릴 사람과 합칠 후보 — 지금 살아 있는 계정 전부 */
interface Ghost {
    uuid: string;
    nickname: string;
    /** 아직 본인이 가입하지 않은 계정(명단 동기화가 만들어 둔 빈 계정) */
    unregistered: boolean;
}

/**
 * **추방된 계정 목록.** 되살릴 사람을 고른다.
 *
 * 추방될 때 아이디를 비켜 준 계정은 그대로 되살리면 그 이름으로 로그인할 수 없다. 그래서
 * 되살릴 때 쓸 아이디를 함께 보여 주고 고칠 수 있게 둔다.
 */
export function DeletedAccounts({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
    const [rows, setRows] = useState<DeletedRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [ids, setIds] = useState<Record<string, string>>({});
    const [busy, setBusy] = useState<string | null>(null);
    const [q, setQ] = useState('');
    /**
     * **합칠 계정.** 되살리기는 **반드시** 하나를 고르게 한다.
     *
     * 사람이 나가면 계정이 자동으로 추방되고, 돌아오면 명단 동기화가 빈 계정을 새로 만든다.
     * 그대로 되살리면 한 사람에 계정이 둘이 된다 — 글·얼굴은 옛 쪽에, 게임 아이디·보유몹·휴가는
     * 새 쪽에 갈린 채로. 그래서 "누구의 옛 계정인지"를 짚고 합친다(닉이 같으면 미리 골라 둔다).
     *
     * 빈 계정만이 아니라 **이미 쓰고 있는 계정과도** 합칠 수 있다 — 다른 아이디로 새로 가입해
     * 버린 경우가 그렇다.
     */
    const [ghosts, setGhosts] = useState<Ghost[]>([]);
    const [match, setMatch] = useState<Record<string, string>>({});
    const [done, setDone] = useState('');

    const load = async () => {
        setLoading(true); setErr('');
        try {
            const res = await fetch('/api/auth/users/deleted', { credentials: 'include' });
            const data = await res.json();
            if (!res.ok || !data.users) {
                setErr(data.error || '불러오지 못했습니다.');
            } else {
                setRows(data.users);
                const next: Record<string, string> = {};
                data.users.forEach((u: DeletedRow) => { next[u.uuid] = baseId(u.userId); });
                setIds(next);

                // 살아 있는 계정을 받아 두고, 닉이 같은 것이 있으면 미리 골라 둔다
                try {
                    const all = await fetch('/api/auth/users', { credentials: 'include' }).then((r) => r.json());
                    const list: Ghost[] = (all.users || [])
                        .map((g: any) => ({ uuid: g.uuid, nickname: g.nickname, unregistered: g.registered === false }))
                        // 미가입 계정이 대개 찾는 상대라 위로 올린다
                        .sort((a: Ghost, b: Ghost) => (Number(b.unregistered) - Number(a.unregistered))
                            || a.nickname.localeCompare(b.nickname, 'ko'));
                    setGhosts(list);
                    const picked: Record<string, string> = {};
                    data.users.forEach((u: DeletedRow) => {
                        const same = list.find((g) => g.nickname === u.nickname);
                        if (same) picked[u.uuid] = same.uuid;
                    });
                    setMatch(picked);
                } catch {
                    /* 후보를 못 받아도 되살리기 자체는 된다 */
                }
            }
        } catch {
            setErr('서버에 닿지 못했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const restore = async (u: DeletedRow) => {
        const wanted = (ids[u.uuid] || '').trim();
        const mate = ghosts.find((g) => g.uuid === match[u.uuid]);
        if (!mate) return;
        /*
         * 어느 쪽을 남기느냐가 갈린다 — 사람이 **지금 쓰고 있는 문**을 남긴다.
         *   상대가 쓰고 있는 계정   그쪽을 남기고 옛 기록만 가져온다 (아이디 그대로)
         *   상대가 빈 계정          되살린 계정을 남기고 빈 계정을 흡수한다
         */
        const ask = mate.unregistered
            ? `${u.nickname} 님을 되살려 '${mate.nickname}' (미가입)과 합칠까요?\n아이디: ${wanted || baseId(u.userId)}`
              + `\n\n'${mate.nickname}' 에 붙은 것(게임 아이디·전적·보유몹·휴가)을 되살린 계정으로 옮기고`
              + `\n빈 계정은 지웁니다.`
            : `${u.nickname} 님의 옛 기록을 '${mate.nickname}' 계정으로 합칠까요?`
              + `\n\n'${mate.nickname}' 은 지금 쓰고 있는 계정이라 **그대로 남깁니다** — 아이디도 그대로입니다.`
              + `\n옛 계정에 붙은 전적·글·좋아요·보유몹·휴가를 그쪽으로 옮기고, 옛 문서는 지웁니다.`;
        if (!window.confirm(ask)) return;
        setBusy(u.uuid); setErr(''); setDone('');
        try {
            const res = await fetch('/api/auth/user-restore', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ uuid: u.uuid, userId: wanted, matchUuid: match[u.uuid] || '' })
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                setErr(data.error || '되살리지 못했습니다.');
            } else {
                setRows((prev) => prev.filter((r) => r.uuid !== u.uuid));
                if (data.mergedInto) {
                    // 쓰고 있는 계정이 남았다 — 옛 문서는 그쪽에 흡수됐다
                    setDone(`${u.nickname} 님의 옛 기록을 '${data.mergedInto}' 계정으로 합쳤습니다`
                        + `(아이디 ${data.keptLogin} 그대로).`
                        + (data.merged && data.merged.length ? ` 옮긴 것: ${data.merged.join(', ')}` : ' 옮길 것은 없었습니다.'));
                } else if (data.mergedWith) {
                    setGhosts((prev) => prev.filter((g) => g.uuid !== match[u.uuid]));
                    setDone(`${u.nickname} 님을 되살리고 '${data.mergedWith}' 계정과 합쳤습니다.`
                        + (data.merged && data.merged.length ? ` 옮긴 것: ${data.merged.join(', ')}` : ' 옮길 것은 없었습니다.'));
                } else {
                    setDone(`${u.nickname} 님을 되살렸습니다.`);
                }
                onChanged();
            }
        } catch {
            setErr('서버에 닿지 못했습니다.');
        } finally {
            setBusy(null);
        }
    };

    /** 이 줄에서 고른 상대가 **쓰고 있는 계정**인가 (그러면 그쪽을 남긴다) */
    const intoLive = (u: DeletedRow): boolean => {
        const mate = ghosts.find((g) => g.uuid === match[u.uuid]);
        return !!mate && !mate.unregistered;
    };

    const shown = q.trim()
        ? rows.filter((r) => (r.nickname + ' ' + baseId(r.userId)).toLowerCase().includes(q.trim().toLowerCase()))
        : rows;

    return (
        <div style={BACKDROP} onClick={onClose}>
            <div style={{ ...CARD, minWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={onClose} />
                <div style={{ fontSize: 18, fontWeight: 800, color: '#1976d2', marginBottom: 2 }}>추방된 계정</div>
                <div style={{ fontSize: 12.5, color: '#888', fontWeight: 600, marginBottom: 12, lineHeight: 1.6 }}>
                    <b>누구의 옛 계정인지 골라야</b> 되살릴 수 있습니다 — 고른 계정과 하나로 합칩니다.<br />
                    고른 계정이 <b>쓰고 있는 계정이면 그쪽을 남기고</b> 옛 전적·글만 가져옵니다(아이디 그대로).
                    <b>빈 계정(미가입)이면</b> 옛 계정을 되살리고 빈 계정을 흡수합니다.
                </div>

                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="닉네임이나 아이디로 찾기"
                    style={{ ...FIELD, marginBottom: 12 }}
                />

                <Note text={err} bad />
                <Note text={done} />

                {loading ? (
                    <Loading padding="24px 0" size={24} />
                ) : shown.length === 0 ? (
                    <div style={{ padding: '24px 0', textAlign: 'center', color: '#888', fontWeight: 600 }}>
                        {rows.length === 0 ? '추방된 계정이 없습니다.' : '찾는 계정이 없습니다.'}
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {shown.map((u) => (
                            <div
                                key={u.uuid}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                    borderRadius: 12, border: '1px solid #e9edf3', background: '#fbfcfe'
                                }}
                            >
                                <img loading="lazy"
                                    src={u.profileImg || '/images/default-profile.png'}
                                    alt=""
                                    style={{ width: 32, height: 32, borderRadius: 9, objectFit: 'cover', background: '#e3f0ff', flexShrink: 0 }}
                                    onError={(e) => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                                />
                                <div style={{ width: 120, flexShrink: 0 }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {u.nickname}
                                    </div>
                                    <div style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>
                                        {u.deletedAt ? new Date(u.deletedAt).toLocaleDateString('ko-KR') : '추방일 모름'}
                                    </div>
                                </div>
                                {/* 쓰고 있는 계정과 합치면 그쪽 아이디를 그대로 쓴다 — 새로 정할 것이 없다 */}
                                <input
                                    value={intoLive(u) ? '' : (ids[u.uuid] ?? '')}
                                    onChange={(e) => setIds((prev) => ({ ...prev, [u.uuid]: e.target.value }))}
                                    disabled={intoLive(u)}
                                    placeholder={intoLive(u) ? '아이디는 그대로' : '되살릴 때 쓸 아이디'}
                                    style={{
                                        ...FIELD, flex: 1, padding: '8px 10px', fontSize: 14,
                                        background: intoLive(u) ? '#f4f7fb' : '#fff',
                                        color: intoLive(u) ? '#aaa' : '#222'
                                    }}
                                />
                                {/* 합칠 미가입 계정 — 닉이 같으면 미리 골라 둔다 */}
                                {/* 합칠 계정 — 고르지 않으면 되살리지 않는다 (누구인지 짚고 넘어간다) */}
                                <select
                                    value={match[u.uuid] ?? ''}
                                    onChange={(e) => setMatch((prev) => ({ ...prev, [u.uuid]: e.target.value }))}
                                    title="이 사람의 지금 계정을 고르세요. 되살린 계정과 하나로 합칩니다"
                                    style={{
                                        ...FIELD, width: 170, flexShrink: 0, padding: '8px 6px', fontSize: 13,
                                        color: match[u.uuid] ? '#1976d2' : '#c62828',
                                        borderColor: match[u.uuid] ? '#dfe3e8' : 'rgba(198,40,40,0.4)',
                                        fontWeight: 700
                                    }}
                                >
                                    <option value="">합칠 계정 고르기</option>
                                    {ghosts.map((g) => (
                                        <option key={g.uuid} value={g.uuid}>
                                            {g.nickname}{g.unregistered ? ' (미가입)' : ''}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={() => restore(u)}
                                    disabled={busy === u.uuid || !match[u.uuid] || (!intoLive(u) && !(ids[u.uuid] || '').trim())}
                                    title={match[u.uuid] ? '' : '합칠 계정을 먼저 고르세요'}
                                    style={button('#2e7d32', busy !== u.uuid && !!match[u.uuid] && (intoLive(u) || !!(ids[u.uuid] || '').trim()))}
                                >{intoLive(u) ? '기록 합치기' : '되살리기'}</button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
